import * as THREE from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import type { Eje } from "./ui.js";

const NORMALES: Record<Eje, THREE.Vector3> = {
  x: new THREE.Vector3(-1, 0, 0),
  y: new THREE.Vector3(0, -1, 0),
  z: new THREE.Vector3(0, 0, -1),
};

/**
 * Plano de seccion global, arrastrable en X/Y/Z.
 *
 * Se usa `renderer.clippingPlanes` (recorte global) en vez de asignar el plano a cada
 * material: los materiales de fragments los crea el worker y van apareciendo con el
 * streaming de tiles, asi que ir material por material dejaria tiles sin recortar.
 */
export class PlanoDeSeccion {
  readonly plano = new THREE.Plane(NORMALES.y.clone(), 0);

  private readonly renderer: THREE.WebGLRenderer;
  private readonly escena: THREE.Scene;
  private readonly tirador = new THREE.Object3D();
  private readonly gizmo: TransformControls;
  private readonly ayudaRaiz: THREE.Object3D;
  private readonly cuadro: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly contorno: THREE.LineSegments<
    THREE.EdgesGeometry,
    THREE.LineBasicMaterial
  >;
  private readonly grupo = new THREE.Group();
  private readonly onCambio: () => void;

  private caja = new THREE.Box3(new THREE.Vector3(-1, -1, -1), new THREE.Vector3(1, 1, 1));
  private eje: Eje = "y";
  private activa = false;

  constructor(
    renderer: THREE.WebGLRenderer,
    escena: THREE.Scene,
    camara: THREE.PerspectiveCamera,
    lienzo: HTMLCanvasElement,
    onCambio: () => void,
    onArrastre: (arrastrando: boolean) => void,
  ) {
    this.renderer = renderer;
    this.escena = escena;
    this.onCambio = onCambio;

    this.cuadro = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: 0x1d4ed8,
        transparent: true,
        opacity: 0.08,
        side: THREE.DoubleSide,
        depthWrite: false,
        // El propio visor del plano no se recorta a si mismo.
        clippingPlanes: [],
      }),
    );
    this.contorno = new THREE.LineSegments(
      new THREE.EdgesGeometry(this.cuadro.geometry),
      new THREE.LineBasicMaterial({ color: 0x1d4ed8, transparent: true, opacity: 0.5 }),
    );
    this.cuadro.add(this.contorno);
    this.grupo.add(this.tirador);
    this.tirador.add(this.cuadro);
    this.grupo.visible = false;
    escena.add(this.grupo);

    this.gizmo = new TransformControls(camara, lienzo);
    this.gizmo.setMode("translate");
    this.gizmo.space = "local";
    this.gizmo.setSize(0.8);
    this.gizmo.attach(this.tirador);
    this.ayudaRaiz = this.gizmo.getHelper();
    this.ayudaRaiz.visible = false;
    escena.add(this.ayudaRaiz);

    this.gizmo.addEventListener("objectChange", () => {
      this.sincronizarPlano();
      this.onCambio();
    });
    this.gizmo.addEventListener("dragging-changed", (evento) => {
      onArrastre(evento.value === true);
    });

    this.aplicarEje("y");
  }

  /** Recalcula tamano y posicion inicial cuando ya se conoce la caja del modelo. */
  ajustarAlModelo(caja: THREE.Box3): void {
    this.caja = caja.clone();
    this.aplicarEje(this.eje);
  }

  fijarEje(eje: Eje): void {
    this.aplicarEje(eje);
    this.onCambio();
  }

  alternar(activa: boolean): void {
    this.activa = activa;
    this.grupo.visible = activa;
    this.ayudaRaiz.visible = activa;
    this.gizmo.enabled = activa;
    this.renderer.clippingPlanes = activa ? [this.plano] : [];
    this.onCambio();
  }

  get estaActiva(): boolean {
    return this.activa;
  }

  private aplicarEje(eje: Eje): void {
    this.eje = eje;

    const centro = this.caja.getCenter(new THREE.Vector3());
    const tamano = this.caja.getSize(new THREE.Vector3());
    const normal = NORMALES[eje];

    // El cuadro se dimensiona con las dos dimensiones perpendicular al corte, con holgura.
    const lado = ((): { ancho: number; alto: number } => {
      if (eje === "x") return { ancho: tamano.z, alto: tamano.y };
      if (eje === "y") return { ancho: tamano.x, alto: tamano.z };
      return { ancho: tamano.x, alto: tamano.y };
    })();
    const holgura = 1.08;
    this.cuadro.scale.set(
      Math.max(lado.ancho * holgura, 0.001),
      Math.max(lado.alto * holgura, 0.001),
      1,
    );

    // El cuadro es un PlaneGeometry en XY: hay que girarlo para que su +Z sea la normal.
    this.cuadro.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

    this.tirador.position.copy(centro);
    // Solo se arrastra a lo largo de la normal; los otros dos ejes quedan bloqueados.
    this.gizmo.showX = eje === "x";
    this.gizmo.showY = eje === "y";
    this.gizmo.showZ = eje === "z";

    this.contorno.geometry.dispose();
    this.contorno.geometry = new THREE.EdgesGeometry(this.cuadro.geometry);

    this.plano.normal.copy(normal);
    this.sincronizarPlano();
  }

  private sincronizarPlano(): void {
    // El plano pasa por el tirador con la normal del eje activo.
    this.plano.setFromNormalAndCoplanarPoint(this.plano.normal, this.tirador.position);
  }

  dispose(): void {
    this.renderer.clippingPlanes = [];
    this.gizmo.detach();
    this.gizmo.dispose();
    this.escena.remove(this.ayudaRaiz);
    this.escena.remove(this.grupo);
    this.cuadro.geometry.dispose();
    this.cuadro.material.dispose();
    this.contorno.geometry.dispose();
    this.contorno.material.dispose();
  }
}
